import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, RefreshCw, Plus, Search, Users, Calendar, Building,
  Download, Trash2, Edit, Eye, UserPlus, X, Check, FileText
} from 'lucide-react';

import { API_URL } from '../utils/api';

const FacilitatorRosterAdmin = () => {
  const { user, isAuthenticated, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Create/Edit dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRoster, setEditingRoster] = useState(null);
  const [formData, setFormData] = useState({
    facilitator_name: '',
    facilitator_email: '',
    organization: '',
    event_name: '',
    notes: ''
  });
  
  // View roster dialog
  const [viewingRoster, setViewingRoster] = useState(null);
  const [rosterDetails, setRosterDetails] = useState(null);
  
  // Add sessions dialog
  const [showAddSessions, setShowAddSessions] = useState(false);
  const [addSessionsRosterId, setAddSessionsRosterId] = useState(null);
  const [sessionCodesToAdd, setSessionCodesToAdd] = useState('');
  
  // Check auth
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isAdmin, authLoading, navigate]);
  
  // Fetch rosters
  const fetchRosters = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters?${params.toString()}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setRosters(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch rosters:', error);
      toast.error('Failed to load rosters');
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);
  
  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchRosters();
    }
  }, [fetchRosters, isAuthenticated, isAdmin]);
  
  // Create roster
  const createRoster = async () => {
    if (!formData.facilitator_name.trim()) {
      toast.error('Facilitator name is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Roster created');
        setShowCreateDialog(false);
        resetForm();
        fetchRosters();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create roster');
      }
    } catch (error) {
      toast.error('Error creating roster');
    }
  };
  
  // Update roster
  const updateRoster = async () => {
    if (!editingRoster) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${editingRoster.roster_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Roster updated');
        setEditingRoster(null);
        resetForm();
        fetchRosters();
      } else {
        toast.error('Failed to update roster');
      }
    } catch (error) {
      toast.error('Error updating roster');
    }
  };
  
  // Delete roster
  const deleteRoster = async (rosterId) => {
    if (!window.confirm('Are you sure you want to delete this roster?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${rosterId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Roster deleted');
        fetchRosters();
      } else {
        toast.error('Failed to delete roster');
      }
    } catch (error) {
      toast.error('Error deleting roster');
    }
  };
  
  // View roster details
  const viewRoster = async (roster) => {
    setViewingRoster(roster);
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${roster.roster_id}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        setRosterDetails(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load roster details');
    }
  };
  
  // Add sessions to roster
  const addSessionsToRoster = async () => {
    if (!addSessionsRosterId || !sessionCodesToAdd.trim()) {
      toast.error('Enter session codes');
      return;
    }
    
    // Parse session codes (comma or newline separated)
    const codes = sessionCodesToAdd
      .split(/[,\n]/)
      .map(c => c.trim())
      .filter(c => c.length > 0);
    
    if (codes.length === 0) {
      toast.error('No valid session codes');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${addSessionsRosterId}/sessions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_codes: codes })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowAddSessions(false);
        setSessionCodesToAdd('');
        setAddSessionsRosterId(null);
        fetchRosters();
        // Refresh viewing roster if open
        if (viewingRoster?.roster_id === addSessionsRosterId) {
          viewRoster(viewingRoster);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add sessions');
      }
    } catch (error) {
      toast.error('Error adding sessions');
    }
  };
  
  // Remove session from roster
  const removeSessionFromRoster = async (rosterId, sessionCode) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${rosterId}/sessions/${sessionCode}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Session removed');
        // Refresh roster details
        if (rosterDetails) {
          viewRoster(viewingRoster);
        }
        fetchRosters();
      } else {
        toast.error('Failed to remove session');
      }
    } catch (error) {
      toast.error('Error removing session');
    }
  };
  
  // Export roster
  const exportRoster = async (rosterId, format = 'csv') => {
    try {
      const response = await fetch(`${API_URL}/api/admin/facilitator-rosters/${rosterId}/export?format=${format}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `roster_${rosterId}_sessions.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Exported as ${format.toUpperCase()}`);
      } else {
        toast.error('Failed to export');
      }
    } catch (error) {
      toast.error('Error exporting');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      facilitator_name: '',
      facilitator_email: '',
      organization: '',
      event_name: '',
      notes: ''
    });
  };
  
  // Open edit dialog
  const openEditDialog = (roster) => {
    setEditingRoster(roster);
    setFormData({
      facilitator_name: roster.facilitator_name || '',
      facilitator_email: roster.facilitator_email || '',
      organization: roster.organization || '',
      event_name: roster.event_name || '',
      notes: roster.notes || ''
    });
  };
  
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div data-testid="facilitator-roster-admin" className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/reflect')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-heading font-bold text-xl flex items-center gap-2">
                <Users className="w-6 h-6 text-violet-400" />
                Facilitator Roster Log
              </h1>
              <p className="text-xs text-muted-foreground">Track session codes by facilitator</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={fetchRosters}
              variant="outline"
              size="sm"
              className="rounded-full"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Roster
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, organization, or event..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 rounded-xl bg-muted/50 border-transparent"
          />
        </div>
        
        {/* Rosters List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 rounded-2xl bg-card/50 animate-pulse" />
            ))}
          </div>
        ) : rosters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rosters.map((roster) => (
              <div 
                key={roster.roster_id}
                className="p-5 rounded-2xl bg-card/50 border border-white/10 hover:border-violet-500/30 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{roster.facilitator_name}</h3>
                    {roster.facilitator_email && (
                      <p className="text-sm text-muted-foreground">{roster.facilitator_email}</p>
                    )}
                  </div>
                  <Badge className="bg-violet-500/20 text-violet-400 border-0">
                    {roster.session_count} sessions
                  </Badge>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {roster.organization && (
                    <Badge variant="outline" className="border-white/20">
                      <Building className="w-3 h-3 mr-1" />
                      {roster.organization}
                    </Badge>
                  )}
                  {roster.event_name && (
                    <Badge variant="outline" className="border-white/20">
                      <Calendar className="w-3 h-3 mr-1" />
                      {roster.event_name}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => viewRoster(roster)}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-white/20 flex-1"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  <Button
                    onClick={() => {
                      setAddSessionsRosterId(roster.roster_id);
                      setShowAddSessions(true);
                    }}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => openEditDialog(roster)}
                    size="sm"
                    variant="ghost"
                    className="rounded-full hover:bg-white/10"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => deleteRoster(roster.roster_id)}
                    size="sm"
                    variant="ghost"
                    className="rounded-full hover:bg-red-500/20 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No facilitator rosters yet</p>
            <p className="text-sm mt-2">Create a roster to start tracking sessions</p>
          </div>
        )}
      </div>
      
      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || !!editingRoster} onOpenChange={(open) => {
        if (!open) {
          setShowCreateDialog(false);
          setEditingRoster(null);
          resetForm();
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingRoster ? 'Edit Roster' : 'Create Facilitator Roster'}</DialogTitle>
            <DialogDescription>
              {editingRoster ? 'Update roster information below.' : 'Add a new facilitator roster to track session codes.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Facilitator Name *</label>
              <Input
                placeholder="Jane Smith"
                value={formData.facilitator_name}
                onChange={(e) => setFormData({ ...formData, facilitator_name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <Input
                type="email"
                placeholder="jane@school.edu"
                value={formData.facilitator_email}
                onChange={(e) => setFormData({ ...formData, facilitator_email: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Organization</label>
              <Input
                placeholder="Lincoln High School"
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Event/Program Name</label>
              <Input
                placeholder="Summer Music Camp 2025"
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <textarea
                placeholder="Additional notes..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full h-20 px-3 py-2 rounded-xl bg-muted/50 border border-white/10 resize-none"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                setEditingRoster(null);
                resetForm();
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={editingRoster ? updateRoster : createRoster}
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              {editingRoster ? 'Save Changes' : 'Create Roster'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Roster Dialog */}
      <Dialog open={!!viewingRoster} onOpenChange={(open) => {
        if (!open) {
          setViewingRoster(null);
          setRosterDetails(null);
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-400" />
              {viewingRoster?.facilitator_name}
            </DialogTitle>
            <DialogDescription>
              View and manage session codes for this facilitator roster.
            </DialogDescription>
          </DialogHeader>
          
          {rosterDetails ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {rosterDetails.organization && (
                  <Badge variant="outline" className="border-white/20">
                    <Building className="w-3 h-3 mr-1" />
                    {rosterDetails.organization}
                  </Badge>
                )}
                {rosterDetails.event_name && (
                  <Badge variant="outline" className="border-white/20">
                    <Calendar className="w-3 h-3 mr-1" />
                    {rosterDetails.event_name}
                  </Badge>
                )}
              </div>
              
              {rosterDetails.notes && (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl">
                  {rosterDetails.notes}
                </p>
              )}
              
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Sessions ({rosterDetails.sessions?.length || 0})</h4>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => exportRoster(viewingRoster.roster_id, 'csv')}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-white/20"
                    disabled={!rosterDetails.sessions?.length}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button
                    onClick={() => exportRoster(viewingRoster.roster_id, 'xlsx')}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-white/20"
                    disabled={!rosterDetails.sessions?.length}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    XLSX
                  </Button>
                </div>
              </div>
              
              {rosterDetails.sessions?.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {rosterDetails.sessions.map((session) => (
                    <div 
                      key={session.session_code}
                      className="p-3 rounded-xl bg-muted/30 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-mono text-sm">{session.session_code}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.creator_label} • {session.audience_mode}
                          {session.completion_status === 'complete' && (
                            <Badge className="ml-2 bg-green-500/20 text-green-500 border-0 text-xs">Complete</Badge>
                          )}
                        </p>
                      </div>
                      <Button
                        onClick={() => removeSessionFromRoster(viewingRoster.roster_id, session.session_code)}
                        size="sm"
                        variant="ghost"
                        className="rounded-full hover:bg-red-500/20 hover:text-red-500"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-6 text-muted-foreground">No sessions in this roster</p>
              )}
              
              <Button
                onClick={() => {
                  setAddSessionsRosterId(viewingRoster.roster_id);
                  setShowAddSessions(true);
                }}
                variant="outline"
                className="w-full rounded-full border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Sessions
              </Button>
            </div>
          ) : (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Sessions Dialog */}
      <Dialog open={showAddSessions} onOpenChange={(open) => {
        if (!open) {
          setShowAddSessions(false);
          setSessionCodesToAdd('');
          setAddSessionsRosterId(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Sessions to Roster</DialogTitle>
            <DialogDescription>
              Enter session codes to add to this facilitator's roster.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Session Codes</label>
              <textarea
                placeholder="Enter session codes (one per line or comma-separated)&#10;ABC123&#10;DEF456"
                value={sessionCodesToAdd}
                onChange={(e) => setSessionCodesToAdd(e.target.value)}
                className="w-full h-32 px-3 py-2 rounded-xl bg-muted/50 border border-white/10 resize-none font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter session codes from student Reflect sessions
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddSessions(false);
                setSessionCodesToAdd('');
                setAddSessionsRosterId(null);
              }}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={addSessionsToRoster}
              className="rounded-full bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Check className="w-4 h-4 mr-2" />
              Add Sessions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilitatorRosterAdmin;
